import PDFBuilder, { fmtDate, fmtDateFromISO, formatCI } from "../../utils/pdfBuilder.js";

const tipTarifLabels = { ora: "pe ora", zi: "pe zi", luna: "pe luna", proiect: "per proiect" };
const termenPlataLabels = {
  factura: "la emiterea facturii",
  factura15: "in termen de 15 zile de la emiterea facturii",
  factura30: "in termen de 30 zile de la emiterea facturii",
  finalizare: "la finalizarea proiectului",
};

export default function buildColaborarePFAPDF(data, filename) {
  const pdf = new PDFBuilder();
  const today = fmtDate(new Date());

  const pfaName = data.pfaDenumire || "________________________";
  const pfaTitular = data.pfaTitular || "________________________";
  const ciVal = data.pfaCI ? formatCI(data.pfaCI) : "___ ___";

  function getBeneficiarName() {
    if (data.beneficiarPF) return data.beneficiarNume || "";
    return data.beneficiarDenumire ? `${data.beneficiarDenumire} prin ${data.beneficiarReprezentant || ""}` : "";
  }

  const tarifText = `${data.tarifSuma || "___"} ${data.tarifMoneda || "RON"} ${tipTarifLabels[data.tipTarif] || ""}`;

  // Header
  pdf.title("Contract de Colaborare / Prestari Servicii", `Nr. ______ din ${today}`);

  // I. PARTILE CONTRACTANTE
  pdf.section("I. PARTILE CONTRACTANTE");

  pdf.pMixed([{ text: "1.1. Prestatorul:", bold: true }]);

  pdf.pMixed([
    { text: "PFA " },
    { text: pfaName, bold: true },
    { text: ", CUI " },
    { text: data.pfaCUI || "________________________", bold: true },
    { text: ", cu sediul profesional in " },
    { text: data.pfaSediu || "________________________", bold: true },
  ], { indent: 5 });

  pdf.pMixed([
    { text: "Reprezentat prin " },
    { text: pfaTitular, bold: true },
    { text: ", CNP " },
    { text: data.pfaCNP || "________________________", bold: true },
    { text: ", CI seria " },
    { text: ciVal, bold: true },
  ], { indent: 5 });

  pdf.italic("si");

  pdf.pMixed([{ text: "1.2. Beneficiarul:", bold: true }]);

  if (data.beneficiarPF) {
    const bciVal = data.beneficiarCI ? formatCI(data.beneficiarCI) : "___ ___";
    pdf.field("Nume", data.beneficiarNume);
    pdf.field("CNP", data.beneficiarCNP);
    pdf.pMixed([
      { text: "CI seria " },
      { text: bciVal, bold: true },
    ], { indent: 5 });
    pdf.field("Domiciliul", data.beneficiarAdresa);
  } else {
    pdf.field("Denumire", data.beneficiarDenumire);
    pdf.field("CUI", data.beneficiarCUI);
    if (data.beneficiarRegCom) pdf.field("Nr. Reg. Com.", data.beneficiarRegCom);
    pdf.field("Sediu social", data.beneficiarSediu);
    pdf.field("Reprezentant legal", data.beneficiarReprezentant);
  }

  pdf.intro("au convenit incheierea prezentului contract de colaborare / prestari servicii in urmatoarele conditii:");

  // II. OBIECTUL CONTRACTULUI
  pdf.section("II. OBIECTUL CONTRACTULUI");

  pdf.article("2.1.", "Prestatorul se obliga sa presteze in favoarea Beneficiarului urmatoarele servicii:");
  pdf.pMixed([{ text: data.obiectContract || "________________________", bold: true }], { indent: 5 });

  pdf.article("2.2.", "Serviciile vor fi prestate in mod independent, Prestatorul nefiind subordonat Beneficiarului. Prestatorul isi organizeaza singur activitatea, programul de lucru si metodele de executie.");

  pdf.article("2.3.", "Prezentul contract NU constituie un raport de munca in sensul Codului Muncii.");

  // III. DURATA CONTRACTULUI
  pdf.section("III. DURATA CONTRACTULUI");

  if (data.durataNedeterminata) {
    pdf.pMixed([
      { text: "3.1. ", bold: true },
      { text: "Prezentul contract se incheie pe " },
      { text: "durata nedeterminata", bold: true },
      { text: ", incepand cu data de " },
      { text: fmtDateFromISO(data.dataInceput), bold: true },
      { text: "." },
    ]);

    pdf.pMixed([
      { text: "3.2. ", bold: true },
      { text: "Oricare dintre parti poate denunta unilateral contractul cu un preaviz de " },
      { text: `${data.preavizReziliere || "30"} zile`, bold: true },
      { text: "." },
    ]);
  } else {
    pdf.pMixed([
      { text: "3.1. ", bold: true },
      { text: "Prezentul contract se incheie pe " },
      { text: "durata determinata", bold: true },
      { text: ", incepand cu data de " },
      { text: fmtDateFromISO(data.dataInceput), bold: true },
      { text: " si pana la data de " },
      { text: fmtDateFromISO(data.dataSfarsit), bold: true },
      { text: "." },
    ]);

    pdf.pMixed([
      { text: "3.2. ", bold: true },
      { text: "Contractul poate fi prelungit prin act aditional semnat de ambele parti." },
    ]);
  }

  // IV. PRETUL SI MODALITATEA DE PLATA
  pdf.section("IV. PRETUL SI MODALITATEA DE PLATA");

  pdf.pMixed([
    { text: "4.1. ", bold: true },
    { text: "Pentru serviciile prestate, Beneficiarul va plati Prestatorului un tarif de " },
    { text: tarifText, bold: true },
    { text: "." },
  ]);

  pdf.pMixed([
    { text: "4.2. ", bold: true },
    { text: "Plata se efectueaza prin transfer bancar, " },
    { text: termenPlataLabels[data.termenPlata] || "conform intelegerii partilor", bold: true },
    { text: "." },
  ]);

  pdf.article("4.3.", "Prestatorul va emite factura fiscala conform legislatiei in vigoare.");

  pdf.article("4.4.", "Intarzierea la plata atrage penalitati de 0.1% pe zi din suma datorata, incepand cu ziua urmatoare scadentei.");

  // V. OBLIGATIILE PRESTATORULUI
  pdf.section("V. OBLIGATIILE PRESTATORULUI");

  const obligatiiPrestator = [
    "Sa presteze serviciile conform specificatiilor convenite in prezentul contract.",
    "Sa respecte termenele stabilite pentru executarea serviciilor.",
    "Sa informeze Beneficiarul cu privire la stadiul executarii serviciilor.",
    "Sa emita factura fiscala pentru serviciile prestate.",
    "Sa isi desfasoare activitatea in mod independent, conform statutului de PFA.",
  ];
  obligatiiPrestator.forEach((text, i) => {
    pdf.pMixed([
      { text: `5.${i + 1}. `, bold: true },
      { text },
    ], { indent: 5 });
  });

  // VI. OBLIGATIILE BENEFICIARULUI
  pdf.section("VI. OBLIGATIILE BENEFICIARULUI");

  const obligatiiBeneficiar = [
    "Sa puna la dispozitia Prestatorului toate informatiile si documentele necesare executarii serviciilor.",
    "Sa plateasca pretul convenit la termenele stabilite.",
    "Sa receptioneze serviciile prestate si sa comunice observatiile in termen de 5 zile lucratoare.",
  ];
  obligatiiBeneficiar.forEach((text, i) => {
    pdf.pMixed([
      { text: `6.${i + 1}. `, bold: true },
      { text },
    ], { indent: 5 });
  });

  // VII. INDEPENDENTA PRESTATORULUI
  pdf.section("VII. INDEPENDENTA PRESTATORULUI");

  pdf.pMixed([
    { text: "7.1. ", bold: true },
    { text: "Prestatorul declara ca desfasoara activitate independenta in baza autorizatiei PFA si nu se afla in raport de subordonare fata de Beneficiar." },
  ], { indent: 5 });

  pdf.pMixed([
    { text: "7.2. ", bold: true },
    { text: "Prestatorul este singur responsabil pentru plata impozitelor, contributiilor sociale (CAS, CASS) si a oricaror alte obligatii fiscale aferente veniturilor obtinute." },
  ], { indent: 5 });

  pdf.pMixed([
    { text: "7.3. ", bold: true },
    { text: "Prestatorul utilizeaza propriile resurse, echipamente si instrumente de lucru." },
  ], { indent: 5 });

  // VIII. INCETAREA CONTRACTULUI
  pdf.section("VIII. INCETAREA CONTRACTULUI");

  pdf.article("8.1.", "Contractul inceteaza:");

  const incetareItems = [
    ...(data.durataNedeterminata ? [] : ["la expirarea duratei"]),
    "prin acordul scris al partilor",
    "prin reziliere pentru neexecutarea obligatiilor contractuale",
    `prin denuntare unilaterala cu preaviz de ${data.preavizReziliere || "30"} zile`,
  ];
  incetareItems.forEach((text, i) => {
    pdf.letterItem("abcd"[i], text);
  });

  pdf.article("8.2.", "In cazul rezilierii din culpa uneia dintre parti, partea in culpa datoreaza daune-interese celeilalte parti.");

  // Dynamic sections — Roman numerals for headings, Arabic for articles
  const roman = ["IX", "X", "XI", "XII", "XIII", "XIV", "XV"];
  let s = 0;
  let a = 9; // Arabic article counter, continues from section VIII

  // Confidentialitate (optional)
  if (data.includeNDA) {
    const heading = roman[s++];
    const n = a++;
    pdf.section(`${heading}. CONFIDENTIALITATE`);

    pdf.pMixed([
      { text: `${n}.1. `, bold: true },
      { text: "Partile se obliga sa pastreze confidentialitatea tuturor informatiilor, documentelor si datelor la care au acces in executarea prezentului contract." },
    ], { indent: 5 });

    pdf.pMixed([
      { text: `${n}.2. `, bold: true },
      { text: "Obligatia de confidentialitate se mentine pe toata durata contractului si pentru o perioada de 2 ani de la incetarea acestuia." },
    ], { indent: 5 });
  }

  // Non-Competitie (optional)
  if (data.includeNonCompete) {
    const heading = roman[s++];
    const n = a++;
    pdf.section(`${heading}. NON-CONCURENTA`);

    pdf.pMixed([
      { text: `${n}.1. `, bold: true },
      { text: "Pe durata contractului si pentru o perioada de " },
      { text: data.nonCompeteDurata || "6 luni", bold: true },
      { text: ` de la incetarea acestuia, Prestatorul se obliga sa nu presteze servicii similare pentru concurenti directi ai Beneficiarului${data.nonCompeteDomeniu ? ` in domeniul ${data.nonCompeteDomeniu}` : ""}.` },
    ], { indent: 5 });

    pdf.pMixed([
      { text: `${n}.2. `, bold: true },
      { text: "Aceasta restrictie se aplica pe teritoriul Romaniei." },
    ], { indent: 5 });
  }

  // Proprietate Intelectuala (always)
  {
    const heading = roman[s++];
    const n = a++;
    pdf.section(`${heading}. PROPRIETATE INTELECTUALA`);

    if (data.transferPI) {
      pdf.pMixed([
        { text: `${n}.1. `, bold: true },
        { text: "Drepturile de proprietate intelectuala asupra rezultatelor serviciilor prestate se transfera integral Beneficiarului la momentul platii integrale." },
      ], { indent: 5 });

      pdf.pMixed([
        { text: `${n}.2. `, bold: true },
        { text: "Prestatorul cedeaza toate drepturile patrimoniale de autor asupra lucrarilor create in executarea prezentului contract." },
      ], { indent: 5 });
    } else {
      pdf.pMixed([
        { text: `${n}.1. `, bold: true },
        { text: "Drepturile de proprietate intelectuala asupra rezultatelor serviciilor prestate raman in proprietatea Prestatorului." },
      ], { indent: 5 });

      pdf.pMixed([
        { text: `${n}.2. `, bold: true },
        { text: "Beneficiarul primeste o licenta neexclusiva de utilizare a rezultatelor, in scopul pentru care au fost create." },
      ], { indent: 5 });
    }
  }

  // Raspundere (always)
  {
    const heading = roman[s++];
    const n = a++;
    pdf.section(`${heading}. RASPUNDERE`);

    pdf.pMixed([
      { text: `${n}.1. `, bold: true },
      { text: "Fiecare parte raspunde pentru prejudiciile cauzate celeilalte parti prin neindeplinirea sau indeplinirea necorespunzatoare a obligatiilor asumate." },
    ], { indent: 5 });

    pdf.pMixed([
      { text: `${n}.2. `, bold: true },
      { text: "Nici una dintre parti nu raspunde pentru neexecutarea obligatiilor cauzata de forta majora, conform legii." },
    ], { indent: 5 });
  }

  // Legea Aplicabila si Litigii (always)
  {
    const heading = roman[s++];
    const n = a++;
    pdf.section(`${heading}. LEGEA APLICABILA SI LITIGII`);

    pdf.pMixed([
      { text: `${n}.1. `, bold: true },
      { text: "Prezentul contract este guvernat de legea romana." },
    ], { indent: 5 });

    pdf.pMixed([
      { text: `${n}.2. `, bold: true },
      { text: "Litigiile se solutioneaza pe cale amiabila, iar in caz contrar de instantele judecatoresti competente de la sediul/domiciliul Prestatorului." },
    ], { indent: 5 });
  }

  // Dispozitii Finale (always)
  {
    const heading = roman[s++];
    const n = a++;
    pdf.section(`${heading}. DISPOZITII FINALE`);

    pdf.pMixed([
      { text: `${n}.1. `, bold: true },
      { text: "Orice modificare a prezentului contract se face prin act aditional semnat de ambele parti." },
    ], { indent: 5 });

    pdf.pMixed([
      { text: `${n}.2. `, bold: true },
      { text: "Prezentul contract s-a incheiat in 2 exemplare originale, cate unul pentru fiecare parte." },
    ], { indent: 5 });
  }

  // Signatures
  pdf.signatures(
    { role: "PRESTATOR", name: pfaName, date: today },
    { role: "BENEFICIAR", name: getBeneficiarName(), date: today },
  );

  pdf.save(filename);
}
